import React from 'react';

/**
 * UploadProgress — inline progress bar + status text for file uploads.
 *
 * Props:
 *   active   — boolean, true while upload is in flight
 *   percent  — 0..100
 *   label    — optional status text (e.g. "Uploading video.mp4")
 *   tone     — 'primary' (red) | 'neutral' (gray). Defaults to primary.
 */
export default function UploadProgress({ active, percent = 0, label = '', tone = 'primary' }) {
  if (!active) return null;
  const barColor = tone === 'neutral' ? 'bg-stone-500' : 'bg-[#AB1C24]';
  const trackColor = tone === 'neutral' ? 'bg-stone-200' : 'bg-[#FDF0ED]';
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div className="space-y-1.5 mt-2" data-testid="upload-progress">
      <div className={`h-2 w-full rounded-full overflow-hidden ${trackColor}`}>
        <div
          className={`h-full ${barColor} transition-all duration-200 ease-out`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-[#57534E]">
        <span className="truncate">{label || 'Wird hochgeladen…'}</span>
        <span className="font-mono tabular-nums font-medium">{clamped}%</span>
      </div>
    </div>
  );
}

/** Helper to build an axios `onUploadProgress` callback that writes into a setState setter. */
export function makeOnProgress(setPercent) {
  return (evt) => {
    if (!evt || !evt.total) return;
    setPercent(Math.round((evt.loaded * 100) / evt.total));
  };
}
